/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as MyTV from 'store/ducks/myTv';
import { Rail } from 'ui/components';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import Constants from 'configs/Constants';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import translate from 'services/i18n/translate';
import { RailMetaData } from 'ui/pages/myTv/MainPage';
import RouterUtils from 'services/utils/RouterUtils';
import { PrivateRoutePath } from 'routes/paths';
import { Asset, EntryPoint } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import { StoreState } from 'store/store';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { useRouter } from '../../../hooks/useRouter';

interface Props {
  rail: RailMetaData;
}

export default function MyTVContinueWatching({ rail }: Props) {
  const dispatch = useDispatch();
  const history = useHistoryWithState();
  const router = useRouter();

  useEffect(() => {
    dispatch(MyTV.ActionCreators.getContinueWatchingAssets());
  }, [dispatch]);

  const onHeaderClick = () => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_WATCH_NEXT, [rail.item.id]));
  };

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({ asset, entryPoint: EntryPoint.WATCH_NEXT, menuItemId });
  };

  const isProcessed = useSelector(MyTV.Selectors.isWatchNextProcessed);

  const watchNextAssets = useSelector((store: StoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(
      store,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.WATCH_NEXT_RAIL,
      MyTV.Selectors.getContinueWatchingAssets,
      EntryPoint.WATCH_NEXT
    )
  );

  return (isProcessed && watchNextAssets && watchNextAssets.length === 0) || !isProcessed || !watchNextAssets ? (
    <ColdStartRail
      railId={rail.item.id}
      railName={translate['firstTimeUse.watchNext.label']}
      railBackgroundImageName={RailBackgroundImageName.WATCH_NEXT}
      coldStartTitle={translate['firstTimeUse.watchNext.title']}
      railDescription={translate['firstTimeUse.watchNext.subtitle']}
      className="no-background"
    />
  ) : (
    <Rail
      railId={rail.item.id}
      menuItemId={rail.item.id}
      className="no-background"
      railName={translate['firstTimeUse.watchNext.label']}
      channelId={rail.item.channelId}
      assets={watchNextAssets}
      hasMore={watchNextAssets && watchNextAssets.length > Constants.MAX_ASSETS_PER_RAIL}
      onHeaderClick={onHeaderClick}
      onCardClick={onCardClick}
    />
  );
}
